import { computed, reactive, watch, type Ref } from 'vue';
import type {
  GridGroup,
  GridVisualRow,
  ItpDataGridColumn,
  ItpDataGridGroupBy,
  ItpDataGridGroupLevel,
} from './types';
import { compilePath } from './gridCell';
import { aggregateRows } from './gridAggregates';
import { collatorFor, compareValues } from './useGridRows';

const KEY_SEPARATOR = '\u0001';

export function normalizeGroupBy(
  groupBy: ItpDataGridGroupBy | undefined | null,
): ItpDataGridGroupLevel[] {
  if (groupBy == null || groupBy === '') {
    return [];
  }
  const list = Array.isArray(groupBy) ? groupBy : [groupBy];
  return (list as readonly (string | ItpDataGridGroupLevel)[])
    .map((entry) => (typeof entry === 'string' ? { field: entry } : entry))
    .filter((level): level is ItpDataGridGroupLevel => !!level?.field);
}

function readerFor(level: ItpDataGridGroupLevel): (row: any) => any {
  return typeof level.valueGetter === 'function'
    ? level.valueGetter
    : compilePath(level.field);
}

function groupKey(parentKey: string, field: string, value: any): string {
  return `${parentKey}${KEY_SEPARATOR}${field}=${String(value)}`;
}

function defaultLabel(value: any, level: ItpDataGridGroupLevel): string {
  if (value == null || value === '') {
    return level.emptyLabel ?? '';
  }
  return String(value);
}

export interface UseGridGroupingOptions {
  rows: Ref<readonly any[]>;
  groupBy: Ref<ItpDataGridGroupBy | undefined>;
  locale?: Ref<string | undefined>;
  storageKey?: Ref<string | null>;
  subtotalColumns?: Ref<readonly ItpDataGridColumn[]>;
}

export function useGridGrouping(options: UseGridGroupingOptions) {
  const { rows, groupBy, locale, storageKey, subtotalColumns } = options;

  const levels = computed(() => normalizeGroupBy(groupBy.value));
  const isGrouped = computed(() => levels.value.length > 0);

  const overrides = reactive<Record<string, boolean>>({});

  // ── Collapse state persistence ──
  const collapseStorageKey = computed(() =>
    storageKey?.value ? `${storageKey.value}:groups` : null,
  );

  function readOverrides(key: string | null): Record<string, boolean> | null {
    if (!key) {
      return null;
    }
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function loadOverrides(key: string | null): void {
    for (const existing of Object.keys(overrides)) {
      delete overrides[existing];
    }
    const stored = readOverrides(key);
    if (!stored) {
      return;
    }
    for (const [groupId, collapsed] of Object.entries(stored)) {
      if (typeof collapsed === 'boolean') {
        overrides[groupId] = collapsed;
      }
    }
  }

  function persistOverrides(): void {
    const key = collapseStorageKey.value;
    if (!key) {
      return;
    }
    try {
      if (Object.keys(overrides).length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(overrides));
      }
    } catch (error) {
      console.warn(
        '[ItpDataGrid] could not save the group collapse state',
        error,
      );
    }
  }

  watch(collapseStorageKey, loadOverrides, { immediate: true });

  function isCollapsed(key: string, level: ItpDataGridGroupLevel): boolean {
    return overrides[key] ?? level.collapsed ?? false;
  }

  // ── Flattening ──
  const visualRows = computed<readonly GridVisualRow[]>(() => {
    const groupLevels = levels.value;
    const data = rows.value;

    if (!groupLevels.length) {
      return data.map((row) => ({ kind: 'data', row }) as GridVisualRow);
    }

    const readers = groupLevels.map(readerFor);
    const collator = collatorFor(locale?.value);
    const output: GridVisualRow[] = [];

    const summarised = subtotalColumns?.value ?? [];
    const totalsFor = (subset: readonly any[]): Record<string, number> =>
      summarised.length ? aggregateRows(subset, summarised) : {};

    const walk = (
      subset: readonly any[],
      depth: number,
      parentKey: string,
    ): void => {
      if (depth >= groupLevels.length) {
        for (const row of subset) {
          output.push({ kind: 'data', row });
        }
        return;
      }

      const level = groupLevels[depth];
      const read = readers[depth];

      const buckets = new Map<string, { value: any; rows: any[] }>();
      for (const row of subset) {
        const value = read(row);
        const bucketId = String(value);
        let bucket = buckets.get(bucketId);
        if (!bucket) {
          bucket = { value, rows: [] };
          buckets.set(bucketId, bucket);
        }
        bucket.rows.push(row);
      }

      const ordered = [...buckets.values()].sort((a, b) =>
        compareValues(a.value, b.value, collator),
      );

      for (const bucket of ordered) {
        const key = groupKey(parentKey, level.field, bucket.value);
        const collapsed = isCollapsed(key, level);
        const group: GridGroup = {
          level: depth,
          key,
          field: level.field,
          value: bucket.value,
          label: defaultLabel(bucket.value, level),
          rows: bucket.rows,
          count: bucket.rows.length,
          collapsed,
          totals: totalsFor(bucket.rows),
        };
        if (level.label) {
          group.label = level.label(group);
        }

        output.push({ kind: 'group', group });

        if (!collapsed) {
          walk(bucket.rows, depth + 1, key);
        }
      }
    };

    walk(data, 0, '');
    return output;
  });

  // ── Commands ──
  function toggleGroup(key: string): void {
    const current = visualRows.value.find(
      (entry): entry is Extract<GridVisualRow, { kind: 'group' }> =>
        entry.kind === 'group' && entry.group.key === key,
    );
    if (!current) {
      return;
    }
    overrides[key] = !current.group.collapsed;
    persistOverrides();
  }

  function setAllCollapsed(collapsed: boolean): void {
    for (const existing of Object.keys(overrides)) {
      delete overrides[existing];
    }
    if (collapsed) {
      for (const entry of visualRows.value) {
        if (entry.kind === 'group' && entry.group.level === 0) {
          overrides[entry.group.key] = true;
        }
      }
    } else {
      let guard = 0;
      let opened = true;
      while (opened && guard < levels.value.length + 1) {
        opened = false;
        for (const entry of visualRows.value) {
          if (entry.kind === 'group' && entry.group.collapsed) {
            overrides[entry.group.key] = false;
            opened = true;
          }
        }
        guard += 1;
      }
    }
    persistOverrides();
  }

  const collapseAll = () => setAllCollapsed(true);
  const expandAll = () => setAllCollapsed(false);

  const hasCollapsedGroups = computed(() =>
    visualRows.value.some(
      (entry) => entry.kind === 'group' && entry.group.collapsed,
    ),
  );

  function resetGroupState(): void {
    for (const existing of Object.keys(overrides)) {
      delete overrides[existing];
    }
    persistOverrides();
  }

  return {
    levels,
    isGrouped,
    visualRows,
    toggleGroup,
    collapseAll,
    expandAll,
    hasCollapsedGroups,
    resetGroupState,
  };
}
